import React, { useEffect, useRef, useState } from 'react';
import { TypeAnimation } from 'react-type-animation';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { decodeJwt } from '@/constant/users/user.constant';
import { APP_BG } from '@/constant/theme/colors.constant';
import { VDI_VERSION } from '@/constant/gatewayTransaction/gatewayTransaction.constant';
import { AUTH_CONSTANTS } from '@/constant/auth/auth.constant';
import { tid } from '@/utils/testId';
import { AUTH_TEST_IDS as TID } from '@/constant/testIds/auth.testIds';
import { LuArrowRight, LuShieldCheck, LuZap, LuGlobe } from 'react-icons/lu';
import Loading from '@/components/atoms/Loader';
import { AppButton } from '@/components/atoms/AppButton';
import { Text, Caption, Heading, Body } from '@/components';
import vertiGaLogo from '../../assets/images/logo/cantaloupe_GA.png';

const Login = () => {
  const navigate = useNavigate();
  const {
    loginWithRedirect,
    isAuthenticated,
    user,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0();
  // Track whether we are in the middle of storing tokens and navigating
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasNavigated = useRef(false);

  useEffect(() => {
    const storeTokenLocally = async () => {
      if (isAuthenticated && user && !hasNavigated.current) {
        hasNavigated.current = true;
        setIsRedirecting(true);
        localStorage.setItem('user', JSON.stringify(user));
        const accessToken = await getAccessTokenSilently();
        const { permissions, scope } = decodeJwt(accessToken);
        localStorage.setItem('permissions', permissions);
        localStorage.setItem('scope', scope);
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('gatewayVersion', JSON.stringify(VDI_VERSION[1]));
        navigate('/dashboard', { replace: true });
      }
    };
    storeTokenLocally();
  }, [isAuthenticated, user]);

  // While Auth0 is initialising, while we are already authenticated (callback
  // landing), or while we are in the process of storing tokens + navigating —
  // show nothing so the login form never flashes on screen.
  if (isLoading || isAuthenticated || isRedirecting) {
    return <Loading />;
  }

  const features = [
    {
      icon: <LuZap className="w-4 h-4" />,
      label: AUTH_CONSTANTS.FEATURES.REALTIME_VDI,
    },
    {
      icon: <LuShieldCheck className="w-4 h-4" />,
      label: AUTH_CONSTANTS.FEATURES.SECURE_ACCESS,
    },
    {
      icon: <LuGlobe className="w-4 h-4" />,
      label: AUTH_CONSTANTS.FEATURES.MULTI_OPERATOR,
    },
  ];

  const version = import.meta.env.VITE_APP_VERSION;
  const heroAnimationSequence = [
    AUTH_CONSTANTS.HERO.ANIMATION_SEQUENCE[0],
    1500,
    AUTH_CONSTANTS.HERO.ANIMATION_SEQUENCE[1],
    1500,
    AUTH_CONSTANTS.HERO.ANIMATION_SEQUENCE[2],
    1500,
    AUTH_CONSTANTS.HERO.ANIMATION_SEQUENCE[3],
    1500,
  ];
  const copyrightText = `${AUTH_CONSTANTS.HERO.COPYRIGHT_PREFIX}${new Date().getFullYear()}${AUTH_CONSTANTS.HERO.COPYRIGHT_SUFFIX}${version ? `${AUTH_CONSTANTS.HERO.COPYRIGHT_VERSION_SEPARATOR}${version}` : ''}`;
  const cardFooter = (
    <>
      {AUTH_CONSTANTS.CARD.FOOTER_PREFIX}{' '}
      <Text as="span" weight="semibold" color="primary">
        {AUTH_CONSTANTS.CARD.FOOTER_BRAND}
      </Text>{' '}
      {AUTH_CONSTANTS.CARD.FOOTER_SUFFIX}
    </>
  );

  return (
    <div
      data-testid={tid(TID.PAGE)}
      className={`min-h-screen w-full flex ${APP_BG.canvas} overflow-hidden`}
    >
      {/* ── Left panel ─────────────────────────────────────────────────────── */}
      <div
        data-testid={tid(TID.LEFT_PANEL)}
        className="relative hidden lg:flex lg:w-2/5 flex-col justify-between p-12 overflow-hidden"
        style={{
          background:
            'linear-gradient(145deg, var(--color-primary), var(--color-secondary) 55%, var(--color-tertiary))',
        }}
      >
        {/* Geometric shapes */}
        <div className="absolute top-[-80px] right-[-80px] w-[320px] h-[320px] rounded-full bg-white/10 pointer-events-none" />
        <div className="absolute bottom-[-100px] left-[-60px] w-[280px] h-[280px] rounded-full bg-black/10 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-white/5 pointer-events-none" />

        {/* Logo */}
        <div>
          <img
            src={vertiGaLogo}
            alt="Cantaloupe"
            className="h-52 brightness-0 invert opacity-90"
          />
        </div>

        {/* Hero text */}
        <div className="flex flex-col gap-6 z-10">
          <div>
            <Heading color="white" size="8xl" leading="tight" tracking="tight">
              <TypeAnimation
                sequence={heroAnimationSequence}
                wrapper="span"
                speed={50}
                repeat={Infinity}
              />
            </Heading>
            <Body
              size="2xl"
              leading="relaxed"
              color="white-soft"
              className="mt-4 max-w-sm"
            >
              {AUTH_CONSTANTS.HERO.BODY_TEXT}
            </Body>
          </div>

          {/* Feature chips */}
          <div className="flex flex-col gap-2.5 mt-2">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center text-white flex-shrink-0">
                  {f.icon}
                </div>
                <Text as="span" size="xl" weight="medium" color="white-soft">
                  {f.label}
                </Text>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <Text as="p" size="base" color="white-dim" className="z-10">
          {copyrightText}
        </Text>
      </div>

      {/* ── Right panel ────────────────────────────────────────────────────── */}
      <div
        data-testid={tid(TID.RIGHT_PANEL)}
        className={`flex-1 flex flex-col relative ${APP_BG.canvas}`}
      >
        {/* ── MOBILE LAYOUT (< lg) ─────────────────────────────────────────── */}
        <div
          className={`lg:hidden relative flex flex-col min-h-screen items-center justify-between px-6 py-14 overflow-hidden ${APP_BG.canvas}`}
        >
          {/* Top: logo + text */}
          <div className="flex flex-col items-center gap-8 flex-1 justify-center z-10">
            <img
              src={vertiGaLogo}
              alt="Cantaloupe"
              className="h-52 w-auto dark:brightness-0 dark:invert opacity-95 drop-shadow-xl"
            />
            <Heading
              color="primary"
              size="5xl"
              leading="tight"
              tracking="tight"
              className="text-center min-h-[3.5rem]"
            >
              <TypeAnimation
                sequence={heroAnimationSequence}
                wrapper="span"
                speed={50}
                repeat={Infinity}
              />
            </Heading>
            <Body
              size="base"
              color="muted"
              align="center"
              className="max-w-[280px]"
            >
              {AUTH_CONSTANTS.HERO.BODY_TEXT}
            </Body>
          </div>

          {/* Bottom: sign-in card */}
          <div
            data-testid={tid(TID.MOBILE_CARD)}
            className={`w-full rounded-3xl ${APP_BG.base} border border-primary/20 p-8 flex flex-col gap-6 shadow-2xl z-10`}
            style={{ borderTop: '3px solid var(--color-primary)' }}
          >
            <div>
              <Heading color="default" size="3xl">
                {AUTH_CONSTANTS.CARD.WELCOME}{' '}
                <Text as="span" size="3xl" weight="bold" color="primary">
                  {AUTH_CONSTANTS.CARD.WELCOME_ACCENT}
                </Text>
              </Heading>
              <Caption color="muted" size="sm" className="mt-1">
                {AUTH_CONSTANTS.CARD.SUBTITLE}
              </Caption>
            </div>
            <AppButton
              data-testid={tid(TID.SIGN_IN_BTN_MOBILE)}
              variant="primary"
              size="lg"
              shape="default"
              fullWidth
              elevation={2}
              trailingIcon={<LuArrowRight />}
              onClick={() => loginWithRedirect()}
              className="!rounded-2xl !h-14 !text-base"
            >
              {AUTH_CONSTANTS.CARD.SIGN_IN_BUTTON}
            </AppButton>
            <Caption align="center" color="muted">
              {cardFooter}
            </Caption>
          </div>

          <Caption color="subtle" className="mt-6 z-10">
            {copyrightText}
          </Caption>
        </div>

        {/* ── DESKTOP LAYOUT (lg+) — unchanged ─────────────────────────────── */}
        <div className="hidden lg:flex flex-1 flex-col items-center justify-center p-8 relative">
          {/* Ambient blob */}
          <div
            className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none opacity-30 dark:opacity-10"
            style={{
              background:
                'radial-gradient(circle, var(--color-primary), transparent 70%)',
            }}
          />

          <div className="w-full max-w-xl flex flex-col gap-8 z-10">
            {/* Card */}
            <div
              data-testid={tid(TID.DESKTOP_CARD)}
              className={`${APP_BG.base} rounded-2xl shadow-xl dark:shadow-black/40 ring-1 ring-black/[0.06] dark:ring-white/[0.06] p-12 flex flex-col gap-8`}
              style={{ borderTop: '3px solid var(--color-primary)' }}
            >
              {/* Header */}
              <div className="flex flex-col gap-2">
                <Text as="h2" size="5xl" weight="bold" color="default">
                  {AUTH_CONSTANTS.CARD.WELCOME}{' '}
                  <Text as="span" size="5xl" weight="bold" color="primary">
                    {AUTH_CONSTANTS.CARD.WELCOME_ACCENT}
                  </Text>
                </Text>
                <Caption color="muted" size="xl">
                  {AUTH_CONSTANTS.CARD.SUBTITLE}
                </Caption>
              </div>

              {/* Divider */}
              <div
                className="h-px"
                style={{
                  background:
                    'linear-gradient(to right, var(--color-primary), var(--color-secondary), transparent)',
                }}
              />

              {/* Login button */}
              <AppButton
                data-testid={tid(TID.SIGN_IN_BTN_DESKTOP)}
                variant="primary"
                size="lg"
                shape="default"
                fullWidth
                elevation={2}
                trailingIcon={<LuArrowRight />}
                onClick={() => loginWithRedirect()}
                className="!rounded-xl !h-14 !text-base"
              >
                {AUTH_CONSTANTS.CARD.SIGN_IN_BUTTON}
              </AppButton>

              {/* Footer note */}
              <Caption align="center" color="muted">
                {cardFooter}
              </Caption>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
